import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

/* ──────────────────────────────────────────────────────────────────────────
 * Connected Accounts admin view
 *
 * God-mode view of every Meta connection on the platform. Used by support
 * to debug "why isn't my data syncing?" issues — surfaces token expiry,
 * per-account sync status, and lets us force re-sync or disconnect.
 * ────────────────────────────────────────────────────────────────────────── */

interface ConnectedAccount {
  id: number;
  accountId: string;
  accountName: string;
  accountStatus: number | null;
  currency: string | null;
  timezone: string | null;
  isActive: boolean | null;
  lastSyncAt: string | null;
  syncStatus: string | null;
  syncErrorMessage: string | null;
  unifiedAccountId: number | null;
  linkedBrands: string[];
}

interface Connection {
  id: number;
  user: { id: number; email: string; name: string | null } | null;
  userId: number;
  fbUserId: string;
  fbUserName: string | null;
  fbUserEmail: string | null;
  status: string | null;
  scopes: string[] | null;
  tokenExpiresAt: string | null;
  isExpired: boolean;
  daysToExpiry: number | null;
  errorMessage: string | null;
  createdAt: string;
  lastSyncAt: string | null;
  accountCount: number;
  accounts: ConnectedAccount[];
  isDemo: boolean;
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

function useConnections() {
  return useQuery({
    queryKey: ["/api/admin/connections"],
    queryFn: () => fetchJson<{ rows: Connection[]; count: number }>("/api/admin/connections"),
  });
}

function useTriggerSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: number) =>
      fetchJson(`/api/admin/connections/accounts/${accountId}/sync`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/admin/connections"] }),
  });
}

function useDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: number) =>
      fetchJson(`/api/admin/connections/${connectionId}/disconnect`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/admin/connections"] }),
  });
}

function StatusPill({ tone, label }: { tone: "ok" | "warn" | "danger" | "muted"; label: string }) {
  const colors = {
    ok: { bg: "#DCFCE7", fg: "#166534" },
    warn: { bg: "#FEF3C7", fg: "#92400E" },
    danger: { bg: "#FEE2E2", fg: "#991B1B" },
    muted: { bg: "#F3F4F6", fg: "#4B5563" },
  }[tone];
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium" style={{ background: colors.bg, color: colors.fg }}>
      {label}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function fmtDaysAgo(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / 3600000);
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return fmtDate(iso);
}

export default function ConnectedAccountsPage() {
  const { toast } = useToast();
  const [filter, setFilter] = useState<"all" | "real" | "demo" | "expired" | "errored">("all");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const { data, isLoading, error } = useConnections();
  const triggerSync = useTriggerSync();
  const disconnect = useDisconnect();

  const rows = data?.rows ?? [];
  const filtered = rows.filter(r => {
    if (filter === "real") return !r.isDemo;
    if (filter === "demo") return r.isDemo;
    if (filter === "expired") return r.isExpired;
    if (filter === "errored") return r.status === "error" || r.accounts.some(a => a.syncStatus === "error");
    return true;
  });

  function toggle(id: number) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  }

  async function onTriggerSync(account: ConnectedAccount) {
    try {
      await triggerSync.mutateAsync(account.id);
      toast({ title: "Sync triggered", description: `${account.accountName} syncing in background` });
    } catch (e: any) {
      toast({ title: "Sync failed", description: e.message, variant: "destructive" });
    }
  }

  async function onDisconnect(conn: Connection) {
    const userLabel = conn.user?.email ?? `user ${conn.userId}`;
    const confirmed = window.confirm(
      `Disconnect ${conn.fbUserName ?? "Meta connection"} for ${userLabel}?\n\n` +
      `This will cascade-delete:\n` +
      `  • ${conn.accountCount} ad account(s)\n` +
      `  • All campaigns, ad sets, ads under those accounts\n` +
      `  • All daily insights\n` +
      `  • Brand-account links\n\n` +
      `This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await disconnect.mutateAsync(conn.id);
      toast({ title: "Disconnected", description: `${conn.fbUserName ?? "Connection"} removed` });
    } catch (e: any) {
      toast({ title: "Disconnect failed", description: e.message, variant: "destructive" });
    }
  }

  // Aggregates for header
  const totalConnections = rows.length;
  const realConnections = rows.filter(r => !r.isDemo).length;
  const expiredCount = rows.filter(r => r.isExpired).length;
  const erroredCount = rows.filter(r => r.status === "error" || r.accounts.some(a => a.syncStatus === "error")).length;

  return (
    <div className="p-6 space-y-6" data-testid="admin-connected-accounts">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Connected Accounts</h1>
          <p className="text-sm text-neutral-500 mt-1">All Meta connections across the platform. Use to debug sync issues, force re-sync, or disconnect a customer's account.</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <StatBlock label="Total connections" value={totalConnections} />
        <StatBlock label="Real (non-demo)" value={realConnections} />
        <StatBlock label="Expired tokens" value={expiredCount} tone={expiredCount > 0 ? "danger" : "muted"} />
        <StatBlock label="With errors" value={erroredCount} tone={erroredCount > 0 ? "warn" : "muted"} />
      </div>

      <div className="flex items-center gap-2 text-xs">
        {(["all", "real", "demo", "expired", "errored"] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded transition-colors ${filter === f ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50"}`}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {isLoading && <div className="text-sm text-neutral-500">Loading connections…</div>}
      {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}

      {!isLoading && filtered.length === 0 && (
        <div className="rounded border border-dashed border-neutral-200 bg-white p-12 text-center text-sm text-neutral-500">
          No connections match this filter.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(conn => {
          const isOpen = expanded.has(conn.id);
          return (
            <div key={conn.id} className="rounded border border-neutral-200 bg-white" data-testid={`connection-${conn.id}`}>
              <div
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-neutral-50"
                onClick={() => toggle(conn.id)}
              >
                <div className="text-neutral-400 text-xs w-4">{isOpen ? "▾" : "▸"}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-sm text-neutral-900 truncate">{conn.fbUserName ?? "(unnamed)"}</span>
                    {conn.isDemo && <StatusPill tone="muted" label="demo" />}
                    {conn.status === "active" && !conn.isExpired && <StatusPill tone="ok" label="active" />}
                    {conn.isExpired && <StatusPill tone="danger" label="expired" />}
                    {conn.status === "error" && <StatusPill tone="danger" label="error" />}
                    {conn.daysToExpiry !== null && conn.daysToExpiry < 14 && conn.daysToExpiry >= 0 && (
                      <StatusPill tone="warn" label={`expires in ${conn.daysToExpiry}d`} />
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 truncate">
                    {conn.user?.email ?? `user ${conn.userId}`} · fb:{conn.fbUserId} · {conn.accountCount} account{conn.accountCount === 1 ? "" : "s"} · last sync {fmtDaysAgo(conn.lastSyncAt)}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onDisconnect(conn); }}
                  disabled={disconnect.isPending}
                  className="px-3 py-1.5 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40"
                  data-testid={`disconnect-${conn.id}`}
                >
                  Disconnect
                </button>
              </div>

              {isOpen && (
                <div className="border-t border-neutral-200 px-4 py-3 bg-neutral-50/50 text-xs space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="User" value={conn.user ? `${conn.user.name ?? ""} <${conn.user.email}>` : `(missing) ${conn.userId}`} />
                    <Field label="Facebook ID" value={conn.fbUserId} />
                    <Field label="Connected at" value={fmtDate(conn.createdAt)} />
                    <Field label="Token expires" value={conn.tokenExpiresAt ? `${fmtDate(conn.tokenExpiresAt)} (${conn.daysToExpiry}d)` : "—"} />
                    <Field label="Scopes" value={conn.scopes?.join(", ") ?? "—"} />
                    <Field label="Last sync" value={fmtDate(conn.lastSyncAt)} />
                  </div>
                  {conn.errorMessage && (
                    <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-red-800">
                      <div className="font-medium">Connection error:</div>
                      <div>{conn.errorMessage}</div>
                    </div>
                  )}

                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5 font-semibold">Ad accounts ({conn.accounts.length})</div>
                    <div className="rounded border border-neutral-200 overflow-hidden bg-white">
                      <table className="w-full">
                        <thead className="bg-neutral-50">
                          <tr className="text-left text-[10px] uppercase text-neutral-500">
                            <th className="px-3 py-2 font-semibold">Name</th>
                            <th className="px-3 py-2 font-semibold">Account ID</th>
                            <th className="px-3 py-2 font-semibold">Status</th>
                            <th className="px-3 py-2 font-semibold">Linked brands</th>
                            <th className="px-3 py-2 font-semibold">Last sync</th>
                            <th className="px-3 py-2 font-semibold w-20"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {conn.accounts.map(a => (
                            <tr key={a.id} className="border-t border-neutral-100">
                              <td className="px-3 py-2 font-medium text-neutral-900">{a.accountName}</td>
                              <td className="px-3 py-2 text-neutral-600 font-mono text-[11px]">{a.accountId}</td>
                              <td className="px-3 py-2">
                                {a.syncStatus === "synced" && <StatusPill tone="ok" label="synced" />}
                                {a.syncStatus === "pending" && <StatusPill tone="warn" label="pending" />}
                                {a.syncStatus === "error" && <StatusPill tone="danger" label="error" />}
                                {!a.syncStatus && <StatusPill tone="muted" label="—" />}
                              </td>
                              <td className="px-3 py-2 text-neutral-600">{a.linkedBrands.length > 0 ? a.linkedBrands.join(", ") : "—"}</td>
                              <td className="px-3 py-2 text-neutral-600">{fmtDaysAgo(a.lastSyncAt)}</td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  onClick={() => onTriggerSync(a)}
                                  disabled={triggerSync.isPending || conn.isDemo}
                                  className="px-2 py-1 text-[11px] rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40"
                                  data-testid={`sync-account-${a.id}`}
                                  title={conn.isDemo ? "Demo accounts don't sync" : "Trigger manual sync"}
                                >
                                  Sync
                                </button>
                              </td>
                            </tr>
                          ))}
                          {conn.accounts.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-4 text-center text-neutral-400">No ad accounts under this connection.</td></tr>
                          )}
                          {conn.accounts.some(a => a.syncErrorMessage) && (
                            <tr><td colSpan={6} className="px-3 py-2 bg-red-50 text-red-800">
                              <div className="font-medium text-[11px] mb-1">Sync errors:</div>
                              {conn.accounts.filter(a => a.syncErrorMessage).map(a => (
                                <div key={a.id} className="text-[11px]">• {a.accountName}: {a.syncErrorMessage}</div>
                              ))}
                            </td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatBlock({ label, value, tone = "muted" }: { label: string; value: number; tone?: "muted" | "warn" | "danger" }) {
  const fg = tone === "danger" ? "#991B1B" : tone === "warn" ? "#92400E" : "#1F2937";
  return (
    <div className="rounded border border-neutral-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</div>
      <div className="text-2xl font-semibold mt-1" style={{ color: fg }}>{value}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-0.5">{label}</div>
      <div className="text-neutral-900 break-all">{value}</div>
    </div>
  );
}
